<script setup lang="ts">
import type {
  SubjectRequest,
  WorkExperience,
} from '@/models/current-student-advanced-standing-form/api'
import type { LegacyClassBinding } from '@/components/current-student-advanced-standing-form/shared/types'

defineProps<{
  workExperiences: WorkExperience[]
  rowClass: (workExperience: WorkExperience) => LegacyClassBinding
  rowspan: (workExperience: WorkExperience) => number
  isVerifiedStatus: (status: WorkExperience['experience_verified']) => boolean
  canEditWorkExperience: (workExperience: WorkExperience) => boolean
}>()

const emit = defineEmits<{
  editWorkExperience: [workExperience: WorkExperience]
  addSubjectRequest: [workExperience: WorkExperience]
  deleteSubjectRequest: [workExperience: WorkExperience, request: SubjectRequest]
}>()
</script>

<template>
  <div class="table-responsive">
    <table class="table table-bordered">
      <thead>
        <tr>
          <th class="narrow"></th>
          <th>Description</th>
          <th>Year</th>
          <th colspan="3">La Trobe subjects to request</th>
        </tr>
      </thead>
      <tbody id="WorkExperienceTableBody">
        <template
          v-for="workExperience in workExperiences"
          :key="workExperience.submission_work_experience_id"
        >
          <tr
            :class="rowClass(workExperience)"
          >
            <td :rowspan="rowspan(workExperience)">
              <template v-if="isVerifiedStatus(workExperience.experience_verified)">
                verified
              </template>
              <button v-else-if="canEditWorkExperience(workExperience)" class="btn btn-primary" type="button" @click="emit('editWorkExperience', workExperience)">
                Edit
              </button>
            </td>
            <td :rowspan="rowspan(workExperience)">{{ workExperience.experience_short_description }}</td>
            <td :rowspan="rowspan(workExperience)">{{ workExperience.experience_year }}</td>
            <td class="narrow" :rowspan="rowspan(workExperience)">
              <button
                class="btn btn-primary btn-sm"
                title="Add new subject"
                type="button"
                @click="emit('addSubjectRequest', workExperience)"
              >
                &#x2b;
              </button>
            </td>
            <td v-if="workExperience.subject_request_list.length === 0" colspan="2" class="text-danger">
              &#x2190; Please Add a Subject
            </td>
            <template v-else>
              <td>{{ workExperience.subject_request_list[0]?.subject_code }}</td>
              <td class="narrow">
                <button
                  v-if="workExperience.subject_request_list[0]"
                  class="btn btn-danger btn-sm"
                  title="Delete Subject"
                  type="button"
                  @click="emit('deleteSubjectRequest', workExperience, workExperience.subject_request_list[0])"
                >
                  &#x232b;
                </button>
              </td>
            </template>
          </tr>
          <tr
            v-for="subjectRequest in workExperience.subject_request_list.slice(1)"
            :key="`${workExperience.submission_work_experience_id}-${subjectRequest.subject_code}`"
          >
            <td>{{ subjectRequest.subject_code }}</td>
            <td class="narrow">
              <button
                class="btn btn-danger btn-sm"
                title="Delete Subject"
                type="button"
                @click="emit('deleteSubjectRequest', workExperience, subjectRequest)"
              >
                &#x232b;
              </button>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
